<template>
  <div>
    <div v-if="lessonIsCancelled">
      <div class="alert alert-danger">
        <h3 class="font-heavy m-t-none m-b-sm">Lesson Cancelled</h3>
        <p class="text-sm text-red-600">
          This lesson has been cancelled. Please contact your coach for more information.
        </p>
        <p class="text-sm text-red-600">
          <i class="fa fa-times"></i> Cancellation reason: {{ cancellationReason }}
        </p>
      </div>
    </div>
    <!-- Coach Bookings -->
    <div v-if="coachBookings.length > 0" class="mb-4">
      <div class="flex justify-space-between align-items-center m-b-m">
        <h2 class="m-y-none font-semi">Participants</h2>
        <div v-if="canCancelEntireLesson && !lessonIsCancelled && !lessonIsCompleted" class="flex gap-x-sm">
          <button
            v-if="!showingLessonCancellation"
            class="btn btn-danger btn-outline"
            @click="showLessonCancellationInput"
          >
            <i class="fa fa-times"></i> Cancel Lesson
          </button>
        </div>
      </div>
      <!-- Lesson Cancellation Input -->
      <div v-if="showingLessonCancellation" class="bg-red-50 border border-red-200 rounded p-md m-b-md">
        <h3 class="m-t-none font-heavy m-b-sm text-red-800">Cancel Entire Lesson</h3>
        <p class="text-sm text-red-700 m-b-md">
          This will cancel the lesson for all coaches and wrestlers. All participants will be notified.
        </p>
        <input
          v-model="lessonCancellationReason"
          type="text"
          class="form-control m-b-sm"
          placeholder="Enter cancellation reason (required)"
          ref="lessonCancellationInput"
        />
        <div v-if="lessonCancellationError" class="text-danger text-sm m-b-sm">
          {{ lessonCancellationError }}
        </div>
        <div class="flex flex-col-xs-only gap-sm">
          <button class="btn btn-danger btn-block-xs-only" @click="confirmLessonCancellation">
            Confirm Cancel Lesson
          </button>
          <button class="btn btn-default btn-block-xs-only" @click="hideLessonCancellationInput">
            Cancel
          </button>
        </div>
      </div>
      <div class="flex justify-space-between align-items-center m-b-m">
        <h3 class="m-b-none m-t-sm font-semi">Coach</h3>
      </div>
      <div v-for="eventBooking in coachBookings" class="flex justify-space-between flex-wrap align-items-center m-t-sm">
        <div class="flex gap-x-sm">
          <img v-if="eventBooking.profile.avatar" :src="eventBooking.profile.avatar" class="profile-pic w-10 h-10 rounded-circle" />
          <div v-else class="profile-pic w-10 h-10 rounded-circle bg-gray-200 flex align-items-center justify-center">
          </div>
          <div>
            <p class="m-t-none m-b-xxs">
              {{ eventBooking.profile.display_name }} <spinner :inline="true" v-if="eventBooking.loading === true"></spinner>
            </p>
          </div>
        </div>
        <div class="flex-shrink-0">
          <div v-if="!isEditing(eventBooking)" class="check-in-buttons flex align-items-center gap-x-sm">
            <div v-if="eventBooking.status === 'invited'" class="text-blue-600">
              <i class="fa fa-envelope-o"></i> Invited
            </div>
            <div v-else-if="eventBooking.status === 'confirmed'" class="text-green-700">
              <i class="text-green-600 fa fa-check"></i> Confirmed
            </div>
            <div v-else-if="eventBooking.status === 'cancelled'" class="text-red-700">
              <i class="text-red-600 fa fa-times"></i> Cancelled
            </div>
            <div v-else-if="eventBooking.status === 'declined'" class="text-orange-600">
              <i class="text-orange-600 fa fa-ban"></i> Declined
            </div>
            <div v-else-if="eventBooking.status === 'completed'" class="text-green-800">
              <i class="text-green-800 fa fa-trophy"></i> Completed
            </div>
            <div v-else class="text-gray-600">
              <span>Pending</span>
            </div>
            <button v-if="canEditBooking(eventBooking) && !lessonIsCancelled" class="btn btn-sm btn-default" @click.prevent="startEditing(eventBooking)">
              <i class="fa fa-pencil"></i>
            </button>
          </div>
          <div v-else>
            <div v-if="eventBooking.status === 'invited'" class="flex gap-x-xs">
              <button class="btn btn-primary btn-outline btn-sm" @click="updateStatus(eventBooking, 'confirmed')"><i class="fa fa-check"></i> Confirm Booking</button>
              <button class="btn btn-danger btn-outline btn-sm" @click="updateStatus(eventBooking, 'declined')"><i class="fa fa-times"></i> Decline Booking</button>
            </div>
            <div v-else-if="eventBooking.status === 'confirmed'" class="flex gap-x-xs">
              <button v-if="!eventBooking.showingCancellationInput" class="btn btn-danger btn-outline btn-sm" @click="showCancellationInput(eventBooking)"><i class="fa fa-times"></i> Cancel Booking</button>
              <div v-if="eventBooking.showingCancellationInput" class="m-t-xs">
                <input
                  v-model="eventBooking.cancellationReason"
                  type="text"
                  class="form-control"
                  size="30"
                  placeholder="Enter cancellation reason"
                />
                <div v-if="eventBooking.cancellationError" class="text-danger text-xs m-t-xxs">
                  {{ eventBooking.cancellationError }}
                </div>
                <div class="flex flex-col-xs-only gap-sm">
                  <button class="btn btn-default m-t-xs" @click="hideCancellationInput(eventBooking)">
                    Back
                  </button>
                  <button class="btn btn-danger m-t-xs" @click="confirmCancellation(eventBooking)">
                    Confirm Cancel
                  </button>
                </div>
              </div>
            </div>
            <div v-else-if="eventBooking.status === 'cancelled' || eventBooking.status === 'declined'" class="flex gap-x-xs">
              <button class="btn btn-primary btn-outline btn-sm" @click="updateStatus(eventBooking, 'confirmed')"><i class="fa fa-check"></i> Confirm Booking</button>
              <button class="btn btn-default btn-sm" @click="stopEditing(eventBooking)"><i class="fa fa-times"></i></button>
            </div>
            <div v-else class="flex gap-x-xs">
              <button class="btn btn-primary btn-outline btn-sm" @click="updateStatus(eventBooking, 'confirmed')"><i class="fa fa-check"></i> Confirm Booking</button>
              <button class="btn btn-danger btn-outline btn-sm" @click="updateStatus(eventBooking, 'declined')"><i class="fa fa-times"></i> Decline Booking</button>
              <button class="btn btn-default btn-sm" @click="stopEditing(eventBooking)"><i class="fa fa-times"></i></button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Wrestler Bookings -->
    <div v-if="wrestlerBookings.length > 0" class="m-t-md">
      <h3 class="m-b-m font-semi">Wrestlers</h3>
      <div v-for="eventBooking in wrestlerBookings" class="flex justify-space-between flex-wrap align-items-center m-t-sm">
        <div class="flex gap-x-sm">
          <img v-if="eventBooking.profile.avatar" :src="eventBooking.profile.avatar" class="profile-pic w-10 h-10 rounded-circle" />
          <div v-else class="profile-pic w-10 h-10 rounded-circle bg-gray-200 flex align-items-center justify-center">
          </div>
          <div>
            <p class="m-t-none m-b-xxs">
              <a :href="`/wrestlers/${eventBooking.profile.id}`">
                {{ eventBooking.profile.display_name }} <spinner :inline="true" v-if="eventBooking.loading === true"></spinner>
              </a>
            </p>
            <div class="text-sm text-gray-600 flex flex-col">
              <span v-if="eventBooking.profile.weight_class">{{ eventBooking.profile.weight_class }}</span>
              <span v-if="eventBooking.profile.dob" class="">{{ ageFor(eventBooking.profile.dob) }} years old</span>
              <span v-if="eventBooking.profile.experience" class="">{{ eventBooking.profile.experience }}</span>
            </div>
          </div>
        </div>
        <div class="flex-shrink-0">
          <div v-if="!isEditing(eventBooking)" class="check-in-buttons flex align-items-center gap-x-sm">
            <div v-if="eventBooking.status === 'invited'" class="text-blue-600">
              <i class="fa fa-envelope-o"></i> Invited
            </div>
            <div v-else-if="eventBooking.status === 'confirmed'" class="text-green-700">
              <i class="text-green-600 fa fa-check"></i> Confirmed
            </div>
            <div v-else-if="eventBooking.status === 'cancelled'" class="text-red-700">
              <i class="text-red-600 fa fa-times"></i> Cancelled
            </div>
            <div v-else-if="eventBooking.status === 'declined'" class="text-orange-600">
              <i class="text-orange-600 fa fa-ban"></i> Declined
            </div>
            <div v-else-if="eventBooking.status === 'completed'" class="text-green-800">
              <i class="text-green-800 fa fa-trophy"></i> Completed
            </div>
            <div v-else class="text-gray-600">
              <span>Pending</span>
            </div>
            <button v-if="canEditBooking(eventBooking) && !lessonIsCancelled" class="btn btn-sm btn-default" @click.prevent="startEditing(eventBooking)">
              <i class="fa fa-pencil"></i>
            </button>
          </div>
          <div v-else>
            <div v-if="eventBooking.status === 'invited'" class="flex gap-x-xs">
              <button class="btn btn-primary btn-outline btn-sm" @click="updateStatus(eventBooking, 'confirmed')"><i class="fa fa-check"></i> Confirm Booking</button>
              <button class="btn btn-danger btn-outline btn-sm" @click="updateStatus(eventBooking, 'declined')"><i class="fa fa-times"></i> Decline Booking</button>
            </div>
            <div v-else-if="eventBooking.status === 'confirmed'" class="flex gap-x-xs">
              <button v-if="!eventBooking.showingCancellationInput" class="btn btn-danger btn-outline btn-sm" @click="showCancellationInput(eventBooking)"><i class="fa fa-times"></i> Cancel Booking</button>
              <div v-if="eventBooking.showingCancellationInput" class="m-t-xs">
                <input
                  v-model="eventBooking.cancellationReason"
                  type="text"
                  class="form-control"
                  size="30"
                  placeholder="Enter cancellation reason"
                />
                <div v-if="eventBooking.cancellationError" class="text-danger text-xs m-t-xxs">
                  {{ eventBooking.cancellationError }}
                </div>
                <div class="flex flex-col-xs-only gap-sm">
                  <button class="btn btn-default m-t-xs" @click="hideCancellationInput(eventBooking)">
                    Back
                  </button>
                  <button class="btn btn-danger m-t-xs" @click="confirmCancellation(eventBooking)">
                    Confirm Cancel
                  </button>
                </div>
              </div>
            </div>
            <div v-else-if="eventBooking.status === 'cancelled' || eventBooking.status === 'declined'" class="flex gap-x-xs">
              <button class="btn btn-primary btn-outline btn-sm" @click="updateStatus(eventBooking, 'confirmed')"><i class="fa fa-check"></i> Confirm Booking</button>
              <button class="btn btn-default btn-sm" @click="stopEditing(eventBooking)"><i class="fa fa-times"></i></button>
            </div>
            <div v-else class="flex gap-x-xs">
              <button class="btn btn-primary btn-outline btn-sm" @click="updateStatus(eventBooking, 'confirmed')"><i class="fa fa-check"></i> Confirm Booking</button>
              <button class="btn btn-danger btn-outline btn-sm" @click="updateStatus(eventBooking, 'declined')"><i class="fa fa-times"></i> Decline Booking</button>
              <button class="btn btn-default btn-sm" @click="stopEditing(eventBooking)"><i class="fa fa-times"></i></button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showAddBookings && !lessonIsCancelled && !lessonIsCompleted && !addingWrestlers && (capacityLeftInBillingPlan > 0 || !confirmedBillingPlan)" class="m-t-lg">
      <button class="btn btn-default" @click="addingWrestlers = true"><i class="fa fa-plus"></i> Add wrestlers to this lesson</button>
    </div>
    <div v-if="showAddBookings && addingWrestlers" class="m-t-lg">
      <h2 class="m-b-sm m-t-none">Add wrestlers to this lesson</h2>
      <!-- Selected wrestlers list -->
      <div v-if="selectedWrestlers.length > 0" class="m-b-md">
        <div class="selected-wrestlers">
          <div v-for="(wrestler, index) in selectedWrestlers" :key="`selected-${wrestler.id}`" class="selected-wrestler search-result teammate m-b-sm">
            <div class="hr-line-dashed"></div>
            <div class="row">
              <div class="col-xs-9">
                <div class="flex">
                  <div v-if="wrestler.avatar_thumbnail" class="m-r-m">
                    <div class="image">
                      <img :src="wrestler.avatar_thumbnail" class="profile-pic" alt="profile picture"/>
                    </div>
                  </div>
                  <div>
                    <h3>{{ wrestler.first_name }} {{ wrestler.last_name }}</h3>
                    <p class="search-type-label">
                      <span v-if="wrestler.academic_class">{{ wrestler.academic_class }}<br/></span>
                      <span v-if="wrestler.weight_class">{{ wrestler.weight_class }}<br/></span>
                      <span v-if="wrestler.age">{{ wrestler.age }} years old<br/></span>
                      <span v-if="wrestler.experience">{{ wrestler.experience }}</span>
                    </p>
                  </div>
                </div>
              </div>
              <div class="col-xs-3 text-right">
                <button class="btn btn-outline btn-danger btn-sm" @click="removeWrestler(index)">
                  <i class="fa fa-times"></i> Remove
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="m-t-md">
          <button class="btn btn-primary btn-block w-full-xs-only" @click="confirmBooking">
            Next: Review booking for {{ selectedWrestlers.length }} wrestler{{ selectedWrestlers.length > 1 ? 's' : '' }}
          </button>
          <p class="text-sm text-gray-600 text-center m-t-sm">
            Or add another wrestler below
          </p>
        </div>
      </div>

      <div>
        <search
          class="search-form"
          response-key="wrestlers"
          placeholder="Type wrestler name..."
          :mutate-history="false"
          :allow-add="false"
          :display-all-on-empty="false"
          readable-model-name="wrestler"
          base-url="/api/v1/wrestlers">
        <template v-slot:list="slotProps">
          <div class="wrestler-wrapper" @click="wrestlerClicked(slotProps.result)">
            <wrestler-search-result
                v-bind:result="slotProps.result"
                v-bind:is-selectable="true"
                v-bind:key="slotProps.result.id"
            >
            </wrestler-search-result>
          </div>
        </template>
      </search>
      </div>
    </div>
  </div>
</template>
<script>
import { errorableMix } from './mixins/errorable_mix';
import { appEmbedMix } from './mixins/app_embed_mix';
import FullLoadList from './full_load_list.vue';
import Spinner from './spinner.vue';
import Search from '../../shared/search.vue';
import WrestlerSearchResult from '../../shared/wrestler_search_result.vue';
import moment from 'moment';
import qs from 'qs';

export default {
  // This pulls a list of all event booking requests for an event and shows them.
  name: "event-bookings-list",
  components: {
    FullLoadList,
    Spinner,
    Search,
    WrestlerSearchResult
  },
  props: {
    eventId: {
      type: Number,
      required: true
    },
    existingEventBookings: {
      type: Array,
      required: true
    },
    showAddBookings: {
      type: Boolean,
      default: false
    },
    profilesWithEditableBookings: {
      type: Array,
      required: true
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    currentProfileId: {
      type: Number,
      required: true,
    },
    currentProfileType: {
      type: String,
      required: true,
    },
    lessonStatus: {
      type: String,
      default: 'available'
    },
  },
  mixins: [appEmbedMix, errorableMix],
  data() {
    return {
      loading: true,
      eventBookings: [],
      selectedWrestlers: [], // Store selected wrestlers
      editingBookingIds: {},
      addingWrestlers: false,
      showingLessonCancellation: false,
      lessonCancellationReason: '',
      lessonCancellationError: '',
    }
  },
  created() {
    // Loop through the passed in bookings and add a loading property
    this.eventBookings = this.existingEventBookings.map(booking => {
      booking.loading = false;
      return booking;
    });

    // Initialize editingBookingIds with all bookings set to false
    // For 'invited' status bookings, start in edit mode if they can be edited
    this.eventBookings.forEach(booking => {
      const startInEditMode = booking.status === 'invited' && this.canEditBooking(booking);
      this.$set(this.editingBookingIds, booking.id, startInEditMode);
    });
  },
  computed: {
    lessonIsCancelled() {
      return this.lessonStatus === 'cancelled';
    },
    lessonIsCompleted() {
      return this.lessonStatus === 'completed';
    },
    coachBookings() {
      return _.filter(this.eventBookings, booking => booking.profile_type === 'CoachProfile');
    },
    wrestlerBookings() {
      return _.filter(this.eventBookings, booking => booking.profile_type === 'WrestlerProfile');
    },
    cancellationReason() {
      const cancelledBookings = _.filter(this.eventBookings, booking => booking.status === 'cancelled');
      const reasons = _.uniq(_.map(cancelledBookings, booking => booking.cancellation_reason));
      return _.join(reasons, ', ');
    },
    confirmedWrestlerBookings() {
      return _.filter(this.eventBookings, booking => booking.status === 'confirmed' && booking.profile_type === 'WrestlerProfile');
    },
    confirmedBillingPlan() {
      let booking = this.confirmedWrestlerBookings[0];
      return booking ? booking.billing_plan : null;
    },
    capacityLeftInBillingPlan() {
      if (!this.confirmedBillingPlan) return 0;
      return this.confirmedBillingPlan.max_wrestler_count - this.confirmedWrestlerBookings.length;
    },
    canCancelEntireLesson() {
      // Admins can always cancel the entire lesson in case they are coordinating other coaches
      if (this.isAdmin) return true;

      // Otherwise coaches can cancel the entire lesson and only if it's their own booking
      if (this.currentProfileType !== 'CoachProfile') return false;

      const myCoachBooking = this.coachBookings.find(booking =>
        booking.profile_id === this.currentProfileId
      );

      return !!myCoachBooking;
    },
  },
  methods: {
    canEditBooking(booking) {
      // Can't modify completed bookings
      if (booking.status === 'completed') {
        return false;
      }

      if (this.showAddBookings) {
        return true;
      }

      return this.profilesWithEditableBookings.some(profile => profile.id === booking.profile_id && profile.profile_type === booking.profile_type);
    },
    isEditing(booking) {
      // Invited bookings are always in edit mode if they can be modified
      if (booking.status === 'invited' && this.canEditBooking(booking)) {
        return true;
      }
      return this.editingBookingIds[booking.id] === true;
    },
    startEditing(booking) {
      this.$set(this.editingBookingIds, booking.id, true);
    },
    stopEditing(booking) {
      // For 'invited' status, don't allow canceling edit mode
      if (booking.status === 'invited') {
        return;
      }
      this.$set(this.editingBookingIds, booking.id, false);
    },
    ageFor(dob) {
      return moment().diff(moment(dob), 'years');
    },
    showCancellationInput(booking) {
      this.$set(booking, 'showingCancellationInput', true);
      this.$set(booking, 'cancellationReason', '');
      this.$set(booking, 'cancellationError', '');
    },
    hideCancellationInput(booking) {
      this.$set(booking, 'showingCancellationInput', false);
      this.$set(booking, 'cancellationError', '');
    },
    confirmCancellation(booking) {
      if (!booking.cancellationReason || booking.cancellationReason.trim() === '') {
        this.$set(booking, 'cancellationError', 'Cancellation reason is required.');
        return;
      }
      this.$set(booking, 'cancellationError', '');
      this.updateStatus(booking, 'cancelled', booking.cancellationReason);
      this.hideCancellationInput(booking);
    },
    updateStatus(booking, status, cancellationReason = null) {
      let vm = this;
      // Ensure loading gets slotted in as a reactive property
      vm.$set(booking, 'loading', true);
      const url = vm.$apiService.eventBookingUrl(booking.id);
      const params = { event_booking: { status: status } };
      if (status === 'cancelled') {
        // guard against coach cancellations, which should go into the method below
        if (booking.profile_type === 'CoachProfile') {
          vm.cancelCoachBooking(booking, cancellationReason);
          return;
        }
        params.event_booking.cancelled_by_id = vm.currentProfileId;
        params.event_booking.cancelled_by_type = vm.currentProfileType;
        params.event_booking.cancellation_reason = cancellationReason;
      } else if (status === 'confirmed') {
        params.event_booking.booked_by_id = vm.currentProfileId;
        params.event_booking.booked_by_type = vm.currentProfileType;
        params.event_booking.cancellation_reason = "";
      }
      axios.put(url, params)
          .then((response) => {
            booking.loading = false;
            booking.status = status;
            let countChange = status === 'confirmed' ? 1 : -1;
            vm.$notificationManager.$emit('event-booking-changed', booking.profile_type, countChange);
            vm.$notificationManager.$emit('event-booking-updated');
            vm.$notificationManager.$emit('show-toast', 'Booking updated', true);

            // Reset editing state after successful update
            // Only if the status is no longer 'invited'
            if (status !== 'invited') {
              vm.stopEditing(booking);
            }
          })
          .catch((error) => {
            booking.loading = false;
            console.error(error);
            vm.$notificationManager.$emit('show-toast', 'Booking request update failed', false);
          });
    },
    wrestlerClicked(wrestler) {
      // clear the search results
      this.$notificationManager.$emit('profile-clicked');
      // Check if wrestler is already selected
      if (!this.selectedWrestlers.some(w => w.id === wrestler.id)) {
        this.selectedWrestlers.push(wrestler);
      }
    },
    removeWrestler(index) {
      this.selectedWrestlers.splice(index, 1);
    },
    confirmBooking() {
      if (this.selectedWrestlers.length === 0) {
        this.$notificationManager.$emit('show-toast', 'Please select at least one wrestler', false);
        return;
      }

      const params = {
        wrestler_ids: this.selectedWrestlers.map(w => w.id)
      };
      const encodedParams = qs.stringify(params, { arrayFormat: 'brackets' });

      if (this.forAppEmbed) {
        const url = `${window.location.origin}/app_embeds/events/${this.eventId}/book?${encodedParams}`;
        this.askAppToPush(url, 'Review & Confirm');
      } else {
        const url = `/events/${this.eventId}/book?${qs.stringify(params, { arrayFormat: 'brackets' })}`;
        window.location.href = url;
      }
    },
    showLessonCancellationInput() {
      this.showingLessonCancellation = true;
      this.lessonCancellationReason = '';
      this.lessonCancellationError = '';

      // Focus the input after the DOM updates
      this.$nextTick(() => {
        if (this.$refs.lessonCancellationInput) {
          this.$refs.lessonCancellationInput.focus();
        }
      });
    },
    hideLessonCancellationInput() {
      this.showingLessonCancellation = false;
      this.lessonCancellationError = '';
    },
    confirmLessonCancellation() {
      if (!this.lessonCancellationReason || this.lessonCancellationReason.trim() === '') {
        this.lessonCancellationError = 'Cancellation reason is required.';
        return;
      }

      this.lessonCancellationError = '';

      // Find the current coach's booking to cancel
      let lessonCoachBooking = this.coachBookings.find(booking =>
        booking.profile_id === this.currentProfileId && booking.status === 'confirmed'
      );

      if (!lessonCoachBooking) {
        // Fallback to the first coach booking if not found, which will happen for admins
        // it opens up an edge case if we have multiple coaches, but that is not how it works currently
        lessonCoachBooking = this.coachBookings[0];
      }

      if (!lessonCoachBooking) {
        this.lessonCancellationError = 'No coach booking found to cancel the lesson.';
        return;
      }

      // Cancel the coach booking with the reason - this will trigger model callbacks
      this.cancelCoachBooking(lessonCoachBooking, this.lessonCancellationReason.trim());
    },
    cancelCoachBooking(booking, cancellationReason) {
      let vm = this;
      vm.$set(booking, 'loading', true);

      const url = vm.$apiService.eventBookingUrl(booking.id);
      const params = {
        event_booking: {
          status: 'cancelled',
          cancelled_by_id: vm.currentProfileId,
          cancelled_by_type: vm.currentProfileType,
          cancellation_reason: cancellationReason
        }
      };

      axios.put(url, params)
        .then((response) => {
          // Refresh the entire page to let the model callbacks handle updating all bookings
          vm.$notificationManager.$emit('show-toast', 'Lesson cancelled for all participants', true);

          if (vm.forAppEmbed) {
            vm.askAppToPop({
              name: 'event_booking_cancelled',
              data: {
                event_booking_ids: [booking.id],
                event_id: this.eventId,
                pop_to_root: true,
                alert: {
                  type: 'success',
                  message: 'Private lesson cancelled. All participants have been notified.'
                }
              }
            });
          } else {
            // Small delay to show the success message before refresh
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        })
        .catch((error) => {
          booking.loading = false;
          console.error(error);
          vm.lessonCancellationError = 'Failed to cancel lesson. Please try again.';
          vm.$notificationManager.$emit('show-toast', 'Failed to cancel lesson', false);
        });
    }
  }
};
</script>
